import type {
    User,
    Group,
    Role,
    RequestType,
    Building,
    Pod,
    Region,
    Request,
    Room,
    Site,
    ConnectionRole,
    SupportRequestResponse,
    GenericInfoCard,
    Floor,
    RackRow,
    Rack,
    Notification,
    UUIDLocation,
    NotificationResponseById,
    AllNotificationResponse,
    NotificationThreadResponseById,
    AdminSLAType,
    ExportObject,
    OrganizationData,
    InvitationSelection,
} from './store-interfaces';
import { useOrganizationStore, type AssociationEntity } from './organization';
import { jwtDecode } from 'jwt-decode';
import { instantToWallClock } from '@/lib/site-time-zone';

/**
 * The atNorth organisation is identified by its (unique) display name. There is no stable
 * organisation UUID across environments — the column defaults to a random `UUID()` per env —
 * so the name is the only reliable client-side handle. Mirrors the backend SQL convention
 * `LOWER(name) = 'atnorth'`.
 */
export const ATNORTH_ORGANIZATION_NAME = 'atnorth';

/**
 * Decides which organisation should be active for the signed-in user.
 *
 * Order of preference:
 *  1. the organisation the user explicitly selected before (persisted in localStorage as
 *     `organization_id`) — so deliberately switching into a customer org is always honoured;
 *  2. otherwise the user's home org, atNorth, when they have access to it. A user with access to
 *     several orgs (e.g. an admin) must default to atNorth rather than an arbitrary first entry of
 *     the unordered org list (ITDEVELOP-2919);
 *  3. otherwise the first available organisation (covers customer users with no atNorth access).
 */
export function selectActiveOrganization(
    availableOrganizations: OrganizationData[],
    storedOrganizationId: string | null,
): OrganizationData | undefined {
    const explicitlySelected = storedOrganizationId
        ? availableOrganizations.find((org) => org.organization_id.toString() === storedOrganizationId)
        : undefined;
    if (explicitlySelected) {
        return explicitlySelected;
    }

    const atNorth = findAtNorthOrganization(availableOrganizations);
    return atNorth ?? availableOrganizations[0];
}

/**
 * Finds the atNorth home organisation in a list, by its (case-insensitive) display name.
 * See {@link ATNORTH_ORGANIZATION_NAME} for why the name — not a UUID — is the handle.
 */
export function findAtNorthOrganization(availableOrganizations: OrganizationData[]): OrganizationData | undefined {
    return availableOrganizations.find(isAtNorthOrganization);
}

/**
 * Whether an organisation is the atNorth home organisation. Use it with the *active* organisation
 * to tell "acting as atNorth" apart from "acting inside a customer org": a user with access to
 * several orgs (e.g. an admin) can be either, whatever their roles.
 */
export function isAtNorthOrganization(organization: Pick<OrganizationData, 'name'> | undefined): boolean {
    return organization?.name?.toLowerCase() === ATNORTH_ORGANIZATION_NAME;
}

/**
 * The organisation context to attach (as `X-Organization-Id`) to incident-API requests.
 *
 * In admin/global view, incident operations always act as the atNorth home org so the NOC
 * operator gets the full internal catalog and create flow — independent of any customer
 * organisation left selected in org view (the catalog is scoped server-side by this header,
 * so a lingering customer selection otherwise yields a restricted catalog). Falls back to the
 * selected org when atNorth is not among the user's organisations. Outside admin view the
 * selected org is used unchanged.
 */
export function resolveIncidentRequestOrganization(
    isAdminView: boolean,
    availableOrganizations: OrganizationData[],
    selectedOrganization: OrganizationData | undefined,
): OrganizationData | undefined {
    if (!isAdminView) {
        return selectedOrganization;
    }
    return findAtNorthOrganization(availableOrganizations) ?? selectedOrganization;
}

export function translateRequestDatatoRequestType(requestData: SupportRequestResponse) {
    const translatedArray = new Array<Request>();
    requestData.issues.forEach((request) => {
        translatedArray.push({
            id: request.id,
            type: request.fields.customfield_10010?.requestType?.name ?? 'Unknown',
            reference: request.key,
            summary: request.fields.summary,
            description: request.fields.description?.content?.[0]?.content?.[0]?.text ?? '',
            status: request.fields.status?.name ?? 'Unknown',
            service_project: request.fields.project?.name ?? 'Unknown',
            requester: request.fields.reporter?.displayName ?? 'Unknown',
            response_time: request.fields.customfield_10039?.ongoingCycle?.remainingTime?.millis ?? 0,
            created_at: new Date(request.fields.created),
            updated_at: new Date(request.fields.updated),
        });
    });
    return translatedArray;
}

export function tableSearchAndSort<T extends object, K extends keyof T>(
    items: T[],
    search: string,
    sortColumn: K,
    sortAscending: boolean,
) {
    const returnItems = items
        .filter((item) =>
            Object.values(item).some((value) =>
                String(value ?? '')
                    .toLowerCase()
                    .includes(search.toLowerCase()),
            ),
        )
        .sort((a, b) => {
            const valueA = a[sortColumn];
            const valueB = b[sortColumn];

            if (valueA == null) {
                return sortAscending ? 1 : -1;
            } else if (valueB == null) {
                return sortAscending ? -1 : 1;
            }

            const a1 = String(valueA).toLowerCase();
            const b1 = String(valueB).toLowerCase();
            if (a1 < b1) {
                return sortAscending ? -1 : 1;
            } else if (a1 > b1) {
                return sortAscending ? 1 : -1;
            } else {
                return 0;
            }
        });
    return returnItems;
}

export function formatDate(dateInput: Date | string | number, opts: { timeZone?: string } = {}) {
    // TODO; have this formatDate only accept Date, all data should be Date type before rendering in the UI
    // With a zone, read the calendar day there, not in the browser: a site-local evening can be the
    // viewer's next morning.
    const date = opts.timeZone ? instantToWallClock(new Date(dateInput), opts.timeZone) : new Date(dateInput);

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const day = String(date.getDate()).padStart(2, '0');
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
}

export function formatDateTime(value: Date | string | number, opts: { showZone?: boolean; timeZone?: string } = {}) {
    // for now, make sure its a Date object, TODO: enforce only Date objects in all the UI, right after network requests
    const dateObject = new Date(value);

    const time = formatTime(dateObject, opts);
    const date = formatDate(dateObject, { timeZone: opts.timeZone });

    return `${date} ${time}`;
}

export function formatTime(value: Date | string | number, opts: { showZone?: boolean; timeZone?: string } = {}) {
    // for now, make sure its a Date object, TODO: enforce only Date objects in all the UI, right after network requests
    const { showZone = true, timeZone } = opts;
    const dateObject = new Date(value);

    const options: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
        ...(timeZone ? { timeZone } : {}),
        ...(showZone ? { timeZoneName: 'short' } : {}),
    };
    // en-GB yields named abbreviations for our core regions (GMT / CET / CEST,
    // DST-aware) and an unambiguous GMT±N fallback elsewhere.
    return dateObject.toLocaleTimeString('en-GB', options);
}

export function getTimeZoneAbbrev(value: Date | string | number = new Date(), timeZone?: string): string {
    const parts = new Intl.DateTimeFormat('en-GB', {
        ...(timeZone ? { timeZone } : {}),
        timeZoneName: 'short',
    }).formatToParts(new Date(value));
    return parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
}

export function translateResponseTime(timeStamp: number | null) {
    if (timeStamp === null || timeStamp === undefined) {
        return `null`;
    }
    const diffHours = timeStamp / (1000 * 60 * 60);
    const diffDays = timeStamp / (1000 * 60 * 60 * 24);

    if (Math.abs(diffHours) < 24) return `${Math.round(diffHours)} hours`;
    else if (Math.abs(diffDays) < 7) return `${Math.round(diffDays)} days`;
    return `${Math.round(diffDays / 7)} weeks`;
}

/**
 * Invitations carry the organization roles picked at invite time. The portal presents that as a
 * single Role of org-admin or org-user, matching the organization user tables; returns the 0/1
 * flag UserRoleTagComponent expects.
 */
export function invitationOrgAdminFlag(roles: InvitationSelection[] | null | undefined): 0 | 1 {
    return (roles ?? []).some((role) => role.name?.toLowerCase() === 'org-admin') ? 1 : 0;
}

export function hasGeneralDataAccess(roles: { name: string; organization_role_id: number }[]): boolean {
    const dataTypes = ['power', 'humidity', 'temperature', 'pue', 'energy'];

    return !!roles.some((role) => {
        return dataTypes.some((dataType) => {
            const roleName = `stat-${dataType}`;
            return role.name === roleName;
        });
    });
}

export interface DotNetRequestType {
    id: string;
    jiraId: number;
    name: string;
    description?: string;
    helpText?: string;
    hidden?: boolean;
    requestTypeType?: 'Support' | 'Incident' | string;
    requestTypeKey?: string;
    createdAt?: string;
    modifiedAt?: string;
}

/**
 * org-api (.NET) returns request types in camelCase with a different shape
 * than the legacy `organization-api` endpoint. We map it back to the
 * `RequestType` interface so the existing overview/table renders unchanged.
 *
 * Exported (rather than nested inside the store closure) so it is unit-testable
 * without spinning up Pinia + axios.
 */
export function mapDotNetRequestType(r: DotNetRequestType): RequestType {
    const isIncident = r.requestTypeType === 'Incident';
    return {
        request_type_id: r.jiraId,
        request_type_uuid: r.id,
        name: r.name,
        description: r.description ?? '',
        help_text: r.helpText ?? '',
        hidden: r.hidden ?? false,
        service_desk_id: 0,
        service_desk_name: '',
        groups_count: 0,
        created_at: r.createdAt ? new Date(r.createdAt) : new Date(0),
        modified_at: r.modifiedAt ? new Date(r.modifiedAt) : new Date(0),
        created_by: '',
        modified_by: '',
        request_type_type: isIncident ? 1 : 0,
    };
}

export function translateAssociationArray(associationType: string, data: AssociationEntity[]) {
    const translatedArray = new Array<Record<string, string | number>>();

    switch (associationType) {
        case 'users':
            (data as User[]).forEach((user) => {
                translatedArray.push({
                    user_id: user.ID,
                });
            });
            break;
        case 'groups':
            (data as Group[]).forEach((group) => {
                translatedArray.push({
                    organization_group_id: group.organization_group_id,
                });
            });
            break;
        case 'requesttypes':
            (data as RequestType[]).forEach((requestType) => {
                translatedArray.push({
                    request_type_id: requestType.request_type_id,
                });
            });
            break;
        case 'roles':
            (data as Role[]).forEach((role) => {
                translatedArray.push({
                    organization_role_connection_id: role.organization_role_connection_id,
                });
            });
            break;
        case 'regions':
            (data as Region[]).forEach((location) => {
                translatedArray.push({
                    region_id: location.region_id,
                });
            });
            break;
        case 'sites':
            (data as Site[]).forEach((location) => {
                translatedArray.push({
                    site_id: location.site_id,
                });
            });
            break;
        case 'buildings':
            (data as Building[]).forEach((location) => {
                translatedArray.push({
                    building_id: location.building_id,
                });
            });
            break;
        case 'rooms':
            (data as Room[]).forEach((location) => {
                translatedArray.push({
                    room_id: location.room_id,
                });
            });
            break;
        case 'pods':
            (data as Pod[]).forEach((location) => {
                translatedArray.push({
                    pod_id: location.pod_id,
                });
            });
            break;
        case 'rack_rows':
            (data as RackRow[]).forEach((location) => {
                translatedArray.push({
                    rack_row_id: location.rack_row_id,
                });
            });
            break;
        case 'racks':
            (data as Rack[]).forEach((location) => {
                translatedArray.push({
                    rack_id: location.rack_id,
                });
            });
            break;
        default:
            break;
    }

    return translatedArray;
}

export function translateAssociationItem(associationType: string, data: AssociationEntity) {
    const translatedArray = new Array<Record<string, string | number>>();
    switch (associationType) {
        case 'users':
            translatedArray.push({
                user_id: (data as User).ID,
            });
            break;
        case 'groups':
            translatedArray.push({
                organization_group_id: (data as Group).organization_group_id,
            });
            break;
        case 'roles':
            translatedArray.push({
                organization_role_connection_id: (data as Role).organization_role_connection_id,
            });
            break;
        case 'requesttypes':
            translatedArray.push({
                request_type_id: (data as RequestType).request_type_id,
            });
            break;
        case 'regions':
            translatedArray.push({
                region_id: (data as Region).region_id,
            });
            break;
        case 'sites':
            translatedArray.push({
                site_id: (data as Site).site_id,
            });
            break;
        case 'buildings':
            translatedArray.push({
                building_id: (data as Building).building_id,
            });
            break;
        case 'rooms':
            translatedArray.push({
                room_id: (data as Room).room_id,
            });
            break;
        case 'pods':
            translatedArray.push({
                pod_id: (data as Pod).pod_id,
            });
            break;
        case 'rack_rows':
            translatedArray.push({
                rack_row_id: (data as RackRow).rack_row_id,
            });
            break;
        case 'racks':
            translatedArray.push({
                rack_id: (data as Rack).rack_id,
            });
            break;
        default:
            break;
    }
    return translatedArray;
}

export const getInitials = (firstName: string, lastName?: string) => {
    if (!firstName && !lastName) return 'XX';
    if (!lastName) {
        if (firstName.includes(' ') && firstName.length > 0) {
            const spaceIndex = firstName.lastIndexOf(' ');
            const lastNameInitial = firstName.charAt(spaceIndex + 1);
            return (firstName.charAt(0) + lastNameInitial).toUpperCase();
        } else return (firstName.charAt(0) + firstName.charAt(1)).toUpperCase();
    } else {
        const first = firstName && firstName.length > 0 ? `${firstName.charAt(0)}` : '';
        const last = lastName && lastName.length > 0 ? `${lastName.charAt(0)}` : '';

        return (first + last).toUpperCase();
    }
};

// Type guard functions
export function isUser(data: unknown): data is User {
    return (data as User).ID !== undefined;
}

export function isGroup(data: unknown): data is Group {
    return (data as Group).organization_group_id !== undefined;
}

export function isRole(data: unknown): data is Role {
    return (data as Role).organization_role_id !== undefined;
}

export function isConnectionRole(data: unknown): data is ConnectionRole {
    return (data as ConnectionRole).organization_role !== undefined;
}

export function isRequestType(data: unknown): data is RequestType {
    return (data as RequestType).request_type_id !== undefined;
}

export function isRegion(data: unknown): data is Region {
    return (data as Region).region_id !== undefined;
}

export function isSite(data: unknown): data is Site {
    return (data as Site).site_id !== undefined;
}

export function isFloor(data: unknown): data is Floor {
    return (data as Floor).floor_id !== undefined;
}

export function isBuilding(data: unknown): data is Building {
    return (data as Building).building_id !== undefined;
}

export function isRoom(data: unknown): data is Room {
    return (data as Room).room_id !== undefined;
}

export function isPod(data: unknown): data is Pod {
    return (data as Pod).pod_id !== undefined;
}

export function isRackRow(data: unknown): data is RackRow {
    return (data as RackRow).rack_row_id !== undefined;
}

export function isRack(data: unknown): data is Rack {
    return (data as Rack).rack_id !== undefined;
}

export function isLocation(data: unknown): data is Region | Site | Building | Room | Pod | RackRow | Rack {
    return (
        isRegion(data) ||
        isSite(data) ||
        isBuilding(data) ||
        isRoom(data) ||
        isPod(data) ||
        isRackRow(data) ||
        isRack(data)
    );
}

export async function handleAssociationAdd(data: AssociationEntity[], id: string, associationFrom: string) {
    if (isUser(data[0])) {
        return await useOrganizationStore().addAssociation(associationFrom, id, 'users', data);
    } else if (isGroup(data[0])) {
        return await useOrganizationStore().addAssociation(associationFrom, id, 'groups', data);
    } else if (isRole(data[0])) {
        return await useOrganizationStore().addAssociation(associationFrom, id, 'roles', data);
    } else if (isRequestType(data[0])) {
        return await useOrganizationStore().addAssociation(associationFrom, id, 'requesttypes', data);
    } else if (isLocation(data[0])) {
        for (const location of data.filter(isLocation)) {
            try {
                await useOrganizationStore().addAssociation(associationFrom, id, getLocationType(location), [location]);
            } catch (error) {
                console.error('Failed to add association:', error);
                return { success: false };
            }
        }
        return { success: true };
    } else {
        return { success: false };
    }
}

export async function handleAssociationRemove(data: unknown, id: string, associationFrom: string) {
    if (isUser(data)) {
        await useOrganizationStore().removeAssociation(associationFrom, id, 'users', data);
    } else if (isGroup(data)) {
        await useOrganizationStore().removeAssociation(associationFrom, id, 'groups', data);
    } else if (isRole(data)) {
        await useOrganizationStore().removeAssociation(associationFrom, id, 'roles', data);
    } else if (isRequestType(data)) {
        await useOrganizationStore().removeAssociation(associationFrom, id, 'requesttypes', data);
    } else if (isLocation(data) && getLocationType(data) !== '') {
        await useOrganizationStore().removeAssociation(associationFrom, id, getLocationType(data), data);
    } else console.log('nothing matched', data);
}

// TODO: remove this map functionality since we have connectionId on Roles now
export function mapConnectionRoleToRole(connectionRoles: (ConnectionRole | Role)[]): Role[] {
    return connectionRoles.map((connectionRole) => {
        if (isConnectionRole(connectionRole)) {
            return {
                organization_role_id: connectionRole.organization_role_id,
                name: connectionRole.organization_role.name,
                description: connectionRole.organization_role.description,
                service_level_id: connectionRole.organization_role.service_level_id,
                created_at: connectionRole.organization_role.created_at,
                modified_at: connectionRole.organization_role.modified_at,
                created_by: connectionRole.organization_role.created_by,
                modified_by: connectionRole.organization_role.modified_by,
                organization_role_connection_id: connectionRole.organization_role_connection_id,
                groups_count: 0,
                users_count: 0,
            };
        } else {
            return connectionRole as Role;
        }
    });
}

export function getLocationType(location: Region | Site | Floor | Building | Room | Pod | RackRow | Rack) {
    // the checkers check for types based on region_id, site_id, etc.
    // however a child location has their parents_id, f.x. a room has a building_id
    // so we need to align the check logigh in a ascending order
    if (isRack(location)) return 'racks';
    if (isRackRow(location)) return 'rack_rows';
    if (isPod(location)) return 'pods';
    if (isRoom(location)) return 'rooms';
    if (isFloor(location)) return 'floors';
    if (isFloor(location)) return 'floors';
    if (isBuilding(location)) return 'buildings';
    if (isSite(location)) return 'sites';
    if (isRegion(location)) return 'regions';
    return '';
}

export function sumUpGenericInfoCard(data: Array<GenericInfoCard>) {
    data.forEach((item) => {
        // The API sends the literal 'N/A' for a missing reading; blank it so the card renders empty.
        if ((item.value as unknown) === 'N/A') {
            (item as { value: number | string }).value = '';
            return;
        }
        if (item.group === 'power' || item.group.includes('power')) {
            if (item.value > 1000000) {
                item.value = parseFloat((item.value / 1000000).toFixed(2));
                item.unit = 'MW';
            } else if (item.value > 1000) {
                item.value = parseFloat((item.value / 1000).toFixed(2));
                item.unit = 'KW';
            } else {
                item.value = parseFloat(item.value.toFixed(2));
                item.unit = 'W';
            }
        } else if (item.group === 'temperature' || item.group.includes('temperature')) {
            item.value = parseFloat(item.value.toFixed(2));
        } else if (item.group === 'humidity' || item.group.includes('humidity')) {
            item.value = parseFloat(item.value.toFixed(2));
        }
    });
    return data as Array<GenericInfoCard>;
}

export function sumUpUnitValue(data: Array<{ unit: string; value: number }>) {
    data.forEach((item) => {
        if (item.unit === 'W' || item.unit === 'Wh') {
            if (item.value > 1000000) {
                item.value = parseFloat((item.value / 1000000).toFixed(2));
                item.unit = item.unit === 'W' ? 'MW' : 'MWh';
            } else if (item.value > 1000) {
                item.value = parseFloat((item.value / 1000).toFixed(2));
                item.unit = item.unit === 'W' ? 'KW' : 'KWh';
            } else {
                item.value = parseFloat(item.value.toFixed(2));
            }
        } else if (item.unit === 'KW' || item.unit === 'KWh') {
            if (item.value > 1000) {
                item.value = parseFloat((item.value / 1000).toFixed(2));
                item.unit = item.unit === 'KW' ? 'MW' : 'MWh';
            } else {
                item.value = parseFloat(item.value.toFixed(2));
            }
        } else if (item.unit === 'pue') {
            item.value = parseFloat(item.value.toFixed(2));
        }
    });
    return data;
}

export function countCascadingLocations(regions: Region[]) {
    let count = 0;
    regions.forEach((region) => {
        count += region.sites.length;
        region.sites.forEach((site) => {
            count += site.buildings.length;
            site.buildings.forEach((building) => {
                count += building.rooms.length;
                building.rooms.forEach((room) => {
                    count += room.pods.length;
                });
            });
        });
    });
    return count;
}

export function getAllItemsFromCascade(items: UUIDLocation[]): UUIDLocation[] {
    const findItems = (items: UUIDLocation[]): UUIDLocation[] => {
        let result: UUIDLocation[] = [];
        items.forEach((item) => {
            result.push(item);
            result = result.concat(findItems(item.children));
        });

        return result;
    };

    return findItems(items);
}

export function getLowestLevelCheckedItems(data: UUIDLocation[], checkedList: Set<string>): string[] {
    const result: string[] = [];

    function traverse(items: UUIDLocation[]): boolean {
        let foundCheckedInBranch = false;

        for (const item of items) {
            const isChecked = checkedList.has(item.id);

            // Recursively traverse children first
            const hasCheckedDescendant = traverse(item.children);

            // If this item is checked and no descendants are checked → lowest-level checked
            if (isChecked && !hasCheckedDescendant) {
                result.push(item.id);
            }

            // If either this item or any descendant is checked, mark the branch as active
            if (isChecked || hasCheckedDescendant) {
                foundCheckedInBranch = true;
            }
        }

        return foundCheckedInBranch;
    }

    traverse(data);
    return result;
}

export function getAllCheckedItems(items: UUIDLocation[], checkedList: Set<string>): UUIDLocation[] {
    const result: Map<string, UUIDLocation> = new Map();

    function traverse(items: UUIDLocation[], parents: UUIDLocation[] = []) {
        for (const item of items) {
            const isChecked = checkedList.has(item.id);

            // Recursively check children
            traverse(item.children, [...parents, item]);

            // If this item or any descendant is checked, mark this and all parents
            const hasCheckedChild = item.children.some((child) => result.has(child.id));

            if (isChecked || hasCheckedChild) {
                result.set(item.id, item);
                for (const parent of parents) {
                    result.set(parent.id, parent);
                }
            }
        }
    }

    traverse(items);
    return Array.from(result.values());
}

export const isImage = (file: File) => {
    return file.type.startsWith('image/');
};

export function mapCascadingItemIdToAPIObject(cascadingIds: string[]) {
    return cascadingIds.map((cascadingId) => {
        const [id, type] = cascadingId.split('-');
        return { [`${type.slice(0, -1)}_id`]: id };
    });
}

export function getHighestLevelParent(itemsToRemove: UUIDLocation[]): UUIDLocation[] {
    const isParentOf = (parent: UUIDLocation, child: UUIDLocation): boolean => {
        return parent.children.some((nestedChild: UUIDLocation) => nestedChild.id === child.id);
    };

    // Filter the itemsToRemove array
    return itemsToRemove.filter((item) => {
        // If the item is a parent, keep it; if it's a child, remove it
        return !itemsToRemove.some((potentialParent) => isParentOf(potentialParent, item));
    });
}

// Function to generate a preview URL for a file
export const getFilePreview = (file: File) => {
    return URL.createObjectURL(file);
};

export async function getFilePreviewFromUrl(fileUrl: string) {
    return await fetch(fileUrl)
        .then((response) => response.blob())
        .then((blob) => URL.createObjectURL(blob))
        .catch(() => null);
}

/**
 * Input guard to prevent non-numeric input in input fields
 * @param e KeyboardEvent
 * @param extraKeysAllowed Additional keys to allow in the input field
 */
export function isNumber(e: KeyboardEvent, extra: string[] = []): boolean {
    const keysAllowed: string[] = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].concat(extra);
    const keyPressed: string = e.key;

    if (!keysAllowed.includes(keyPressed)) {
        e.preventDefault();
        return false;
    }
    return true;
}

/**
 * overlapping
 * @param bounds DOMRect object gotten via some element's getBoundingClientRect() method
 * @param x mouse x coordinate
 * @param y mouse y coordinate
 * @param offset some offset to add to the bounds top
 * @returns boolean value whether the mouse coordinates are within the bounds
 */
export const overlapping = (bounds: DOMRect, x: number, y: number, offset = 0) => {
    return (
        x > bounds.left - offset &&
        x < bounds.right + offset &&
        y > bounds.top - offset * 0.5 &&
        y < bounds.bottom + offset
    );
};

/**
 * Append Unit for SLA types
 * @param unit
 * @returns
 */
export const appendUnit = (unit: string) => {
    switch (unit) {
        case '%':
            return '%';
        case 'C':
            return '°C';
        case 'Hours':
            return ' Hours';
        case 'Days':
            return ' Days';
        default:
            return '';
    }
};

export function mapRoleLevelToName(level: number) {
    switch (level) {
        case 1:
            return 'Bronze';
        case 2:
            return 'Silver';
        case 3:
            return 'Gold';
    }
}

export function mapNextStepToAdminRole(nextStep: string) {
    if (['Role Pool', 'Default Groups'].includes(nextStep)) return 'CIDM';
    return 'NOC';
}

export function serviceLevelClass(level: number) {
    switch (level) {
        case 1:
            return 'text-bronze-500 tag-bronze';
        case 2:
            return 'text-silver-500 tag-silver';
        case 3:
            return 'text-gold-500 tag-gold';
    }
}

export function getFriendlyTimeFromSeconds(seconds: number) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours === 0) {
        return `${minutes} min`;
    } else {
        return `${hours} h ${minutes} min`;
    }
}

export function getCPRolesFromToken(token: string): string[] {
    return getClientRolesFromToken(token, 'customer-portal');
}

export function isVisitor(userRoles: string[]): boolean {
    // NOTE: userRoles can sometimes be more than just visitor,
    // if a user is first a visitor then a customer, the user can end up with both roles
    // this is to make sure that the user is only considered a visitor if he is a visitor and visitor only.
    return userRoles.length === 1 && userRoles[0] === 'visitor';
}

export function getOrgRolesFromToken(token: string): string[] {
    return getClientRolesFromToken(token, 'organization-api');
}

export function getOrganizationSchemeRolesFromToken(token: string): string[] {
    return getClientRolesFromToken(token, 'organization-scheme');
}

function getClientRolesFromToken(token: string, clientId: string): string[] {
    if (!token) return [];
    try {
        const decodedToken = jwtDecode<{
            resource_access?: Record<string, { roles?: string[] }>;
        }>(token);
        return decodedToken?.resource_access?.[clientId]?.roles ?? [];
    } catch (error) {
        console.warn(`Invalid token for ${clientId} roles`, error);
        return [];
    }
}

export const groupNotifications = (
    notifications: Notification[],
    filter: ((item: Notification) => boolean) | undefined,
) => {
    // we sort before we group to ensure the order is maintained
    const now = new Date();
    const dates = {
        today: new Date().setHours(0, 0, 0, 0),
        yesterday: new Date().setHours(0, 0, 0, 0) - 86400000,
        thisWeek: new Date().setHours(0, 0, 0, 0) - new Date().getDay() * 86400000,
        lastWeek: new Date().setHours(0, 0, 0, 0) - (new Date().getDay() + 7) * 86400000,
        last2Weeks: new Date().setHours(0, 0, 0, 0) - (new Date().getDay() + 14) * 86400000,
        lastMonth: new Date().setHours(0, 0, 0, 0) - new Date().getDate() * 86400000,
        last2Months: new Date().setHours(0, 0, 0, 0) - (new Date().getDate() + 30) * 86400000,
    };

    const group = Object.groupBy(
        notifications.sort((a, b) => b.severity - a.severity) as Notification[],
        ({ startDate, notificationDate, endDate }) => {
            if (startDate > now) return 'scheduled';

            // note, endDate.setHours mutates endDate, therefore we must copy first new Date(endDate).setHours
            if (!endDate || new Date(endDate).setHours(0, 0, 0, 0) > dates.today) return 'ongoing';
            const creation = new Date(notificationDate).setHours(0, 0, 0, 0);
            if (creation === dates.today) return 'today';
            if (creation >= dates.thisWeek) return 'this week';
            if (creation >= dates.last2Weeks) return 'last 2 weeks';
            if (creation >= dates.lastMonth) return 'last month';
            if (creation >= dates.last2Months) return 'last 2 months';
            return 'earlier';
        },
    );

    // we remove "Global" notifications from the group or other types according to the given filter
    if (filter) {
        for (const key in group) {
            if (key !== 'ongoing') {
                group[key as keyof typeof group] = group[key as keyof typeof group]?.filter(filter);

                if (group[key as keyof typeof group]?.length === 0) {
                    delete group[key as keyof typeof group];
                }
            }
        }
    }

    const sortBy = [
        'ongoing',
        'today',
        'this week',
        'last 2 weeks',
        'last month',
        'last 2 months',
        'earlier',
        'scheduled',
    ];
    const sortedKeys = Object.fromEntries(
        Object.keys(group)
            .sort((a, b) => {
                return sortBy.indexOf(a) - sortBy.indexOf(b);
            })
            .map((key) => [key, group[key as keyof typeof group]]),
    );

    return sortedKeys;
};

export const resToNotification = (res: AllNotificationResponse | NotificationResponseById) =>
    ({
        id: res.notification_id,
        title: res.title,
        type: res.notification_type.name,
        notification_template_id: res.notification_template_id,
        notification_type_id: res.notification_type_id,
        startDate: new Date(res.start_date * 1000),
        endDate: res.end_date ? new Date(res.end_date * 1000) : undefined,
        notificationDate: new Date(res.send_date * 1000),
        fields: (res as NotificationResponseById)?.notification_fields ?? [],
        template: res?.notification_template ?? null,
        acknowledged: res.acknowledged ?? null,
        severity: res.severity,
        creator: res.creator,
    }) as Notification;

export const resToNotificationThread = (res: NotificationThreadResponseById) => ({
    ...resToNotification(res),
    summary: res.notification_template.summary,
});

export const getInlineLocationStringFromCascadeCompare = (
    allLocations: UUIDLocation[],
    targetLocations: string[],
): string[] => {
    const findLocationById = (id: string, locations: UUIDLocation[]): UUIDLocation | undefined => {
        for (const location of locations) {
            if (location.id === id) {
                return location;
            }
            const found = findLocationById(id, location.children);
            if (found) {
                return found;
            }
        }
        return undefined;
    };

    const buildLocationString = (location: UUIDLocation, locations: UUIDLocation[]): string => {
        let locationString = location.name;
        let parent = location.parentId ? findLocationById(location.parentId, locations) : null;
        while (parent) {
            locationString = `${parent.name}/${locationString}`;
            parent = parent.parentId ? findLocationById(parent.parentId, locations) : null;
        }
        return locationString;
    };

    return targetLocations
        .map((id) => {
            const location = findLocationById(id, allLocations);
            return location ? buildLocationString(location, allLocations) : '';
        })
        .filter((locationString) => locationString !== '');
};

export const getInlineLocationStringFromCascade = (allLocations: UUIDLocation[]): string[] => {
    const buildLocationString = (location: UUIDLocation, parentNames: string = ''): string[] => {
        const fullName = parentNames ? `${parentNames} / ${location.name}` : location.name;

        // If no children, return this location as it's the lowest level
        if (location.children.length === 0) {
            return [fullName];
        }

        // Otherwise, process children and pass the fullName down
        return location.children.flatMap((child) => buildLocationString(child, fullName));
    };

    return allLocations.flatMap((location) => buildLocationString(location));
};

export const getLocationByLocationIdFromUUIDCascade = (
    locationId: string,
    locations: UUIDLocation[],
): UUIDLocation | null => {
    for (const location of locations) {
        if (location.id === locationId) {
            return location;
        }
        const found = getLocationByLocationIdFromUUIDCascade(locationId, location.children);
        if (found) {
            return found;
        }
    }
    return null;
};

export const mapSlaTypesToValues = (line: AdminSLAType) => {
    const defaults = { C: '0-0', '%': '0-0', Hours: '0', Days: '0' };
    return {
        sla_type: {
            sla_type_id: line.sla_type_id,
            name: line.name,
            unit: line.name === 'Notification' ? 'Days' : line.unit,
        },
        value: defaults[line.unit],
        sla_tags: line.sla_tags.map((tag) => ({
            sla_tag_id: tag.sla_tag_id,
            name: tag.name,
            value: defaults[line.unit],
        })),
    };
};

export const mapUpDateSpan = (startDate: string, endDate: string) => {
    // Months in desired format
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    function parse(dateStr: string) {
        const date = new Date(dateStr);
        return {
            month: date.getUTCMonth() + 1, // getUTCMonth is 0-based
            year: date.getUTCFullYear(),
        };
    }

    const start = parse(startDate);
    const end = parse(endDate);

    const startMonth = months[start.month - 1];
    const endMonth = months[end.month - 1];

    if (start.year === end.year) {
        if (start.month === end.month) {
            return `${startMonth} ${start.year}`;
        }
        return `${startMonth} - ${endMonth} ${start.year}`;
    } else {
        return `${startMonth} ${start.year} - ${endMonth} ${end.year}`;
    }
};

export function mapUpDataCards(data: GenericInfoCard[], zones: string[]): GenericInfoCard[] {
    const dataArray: GenericInfoCard[] = [];

    zones.forEach((zone) => {
        const zoneData = data; // We don't have the zone in the response anymore and don't need it .filter((item) => item.zone === zone);
        // Power
        const powerSum = zoneData
            .filter((item) => item.group === 'Power')
            .reduce((sum, item) => sum + (item.value || 0), 0);
        if (powerSum) {
            dataArray.push({
                sensorId: '',
                group: 'power',
                sensorName: '',
                time: '',
                building: zone,
                value: powerSum,
                info: 'Current power consumption',
            });
        }

        // Temperature
        const temperatureItems = zoneData.filter(
            (item) => item.group === 'Temperature' && typeof item.value === 'number',
        );
        const temperatureAvg =
            temperatureItems.length > 0
                ? temperatureItems.reduce((sum, item) => sum + item.value, 0) / temperatureItems.length
                : 0;
        if (temperatureItems.length > 0) {
            dataArray.push({
                sensorId: '',
                group: 'temperature',
                sensorName: '',
                time: '',
                building: zone,
                value: temperatureAvg,
                info: 'Current average temperature',
                unit: '°C',
            });
        }

        // Humidity
        const humidityItems = zoneData.filter((item) => item.group === 'Humidity' && typeof item.value === 'number');
        const humidityAvg =
            humidityItems.length > 0
                ? humidityItems.reduce((sum, item) => sum + item.value, 0) / humidityItems.length
                : 0;
        if (humidityItems.length > 0) {
            dataArray.push({
                sensorId: '',
                group: 'humidity',
                sensorName: '',
                time: '',
                building: zone,
                value: humidityAvg,
                info: 'Current average humidity',
                unit: '%',
            });
        }

        // PUE
        const pueItems = zoneData.filter((item) => item.group === 'Environment');
        const pueAvg =
            pueItems.length > 0
                ? parseFloat((pueItems.reduce((sum, item) => sum + item.value, 0) / pueItems.length).toFixed(2))
                : 0;
        if (pueItems.length > 0) {
            dataArray.push({
                sensorId: '',
                group: 'pue',
                sensorName: '',
                time: '',
                building: zone,
                value: pueAvg,
                info: 'Current average pue',
                unit: '',
            });
        }
    });

    return sumUpGenericInfoCard(dataArray);
}

/**
 * Entry point for any export.
 * Decides what to call based on exportType and normalizes data.
 */
export function exportData(exportObject: ExportObject) {
    if (exportObject.exportType === 'csv') {
        // For CSV, we expect `fields` to be an array of arrays.
        // If it’s an array of objects, we map it using CSVHeaders.
        const rows = Array.isArray(exportObject.fields) ? exportObject.fields : Object.values(exportObject.fields);
        exportCSV({
            ...exportObject,
            fields: rows,
        });
    } else if (exportObject.exportType === 'json') {
        // For JSON, ensure we have an object or array of objects.
        const data =
            Array.isArray(exportObject.fields) && Array.isArray(exportObject.fields[0])
                ? convertArrayToObjects(exportObject.fields as string[][], exportObject.CSVHeaders)
                : exportObject.fields;

        exportJSON({
            ...exportObject,
            fields: data,
        });
    } else {
        throw new Error('Unsupported export type');
    }
}

/**
 * Converts array-of-arrays into array-of-objects using headers.
 */
function convertArrayToObjects(rows: string[][], headers?: string[]): Record<string, unknown>[] {
    if (!headers) throw new Error('CSVHeaders required for JSON conversion');
    return rows.map((row) => {
        const obj: Record<string, unknown> = {};
        headers.forEach((header, i) => (obj[header] = row[i]));
        return obj;
    });
}

function exportCSV(exportObject: ExportObject) {
    // if (!exportObject.CSVHeaders || exportObject.CSVHeaders.length === 0) {
    //     throw new Error('CSVHeaders are required for CSV export');
    // }

    const headers = exportObject.CSVHeaders ?? [];
    const rows = exportObject.fields as unknown[][];

    const csvContent = [headers, ...rows]
        .map((e) => e.map((field) => `"${String(field ?? '').replace(/"/g, '""')}"`).join(','))
        .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${exportObject.downloadName}.csv`;
    link.click();
}

function exportJSON(exportObject: ExportObject) {
    const jsonData = exportObject.fields;
    const blob = new Blob([JSON.stringify(jsonData, null, 2)], {
        type: 'application/json',
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${exportObject.downloadName}.json`;
    link.click();
}

export function formatDateToISOString(date: Date): string {
    return date.toISOString().split('.')[0] + 'Z';
}

/**
 * This helper should only be used in the store layer (right after network requests)
 */
export function parseIsoDate(value: string): Date {
    return new Date(value);
}

/**
 * This helper should only be used in the store layer (right after network requests)
 */
export function parseUnixSeconds(value: number): Date {
    return new Date(value * 1000);
}
/**
 * This helper should only be used in the store layer (right after network requests)
 */
export function parseUnixMillis(value: number): Date {
    return new Date(value);
}
